import { z } from 'zod';

import type { Permission } from '@nucleus-commerce/admin-kit/permissions';
import type {
  BulkOrderAction,
  BulkOrdersResult,
  PostAdminV1StoresStoreIdOrdersBulkBody,
} from '@nucleus-commerce/api-client/admin';

/** Most orders one bulk request takes; the backend refuses more with 400. */
export const BULK_MAX = 100;

/** The permission each action needs, per row, as the backend checks it. */
export const BULK_PERMISSION: Record<BulkOrderAction, Permission> = {
  add_tags: 'orders.edit',
  remove_tags: 'orders.edit',
  mark_paid: 'orders.write',
  archive: 'orders.write',
  fulfil: 'fulfillments.write',
  cancel: 'orders.cancel',
};

const HTTP_URL = /^https?:\/\/\S+$/i;

/** One form for every action; the mapper reads only the fields the chosen action uses. */
export function bulkSchema(action: BulkOrderAction) {
  return z
    .object({
      tags: z.array(z.string().trim().min(1).max(60)).max(50),
      reference: z.string().trim().max(200),
      note: z.string().trim().max(1000),
      reason: z.string().trim().max(500),
      restock: z.boolean(),
      refund: z.boolean(),
      carrier: z.string().trim().max(80),
      trackingNumber: z.string().trim().max(120),
      trackingUrl: z
        .string()
        .trim()
        .refine((v) => v === '' || HTTP_URL.test(v), {
          error: 'orders:bulk.validation.trackingUrl',
        }),
      notifyCustomer: z.boolean(),
    })
    .superRefine((v, ctx) => {
      if ((action === 'add_tags' || action === 'remove_tags') && v.tags.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['tags'], message: 'orders:bulk.validation.tags' });
      }
      if (action === 'cancel' && v.reason === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['reason'],
          message: 'orders:cancel.validation.reasonRequired',
        });
      }
    });
}

export type BulkForm = z.input<ReturnType<typeof bulkSchema>>;

export function emptyBulkForm(): BulkForm {
  return {
    tags: [],
    reference: '',
    note: '',
    reason: '',
    restock: true,
    refund: false,
    carrier: '',
    trackingNumber: '',
    trackingUrl: '',
    notifyCustomer: true,
  };
}

/** The payload the backend reads for each action; blanks are left out. */
export function toBulkBody(
  action: BulkOrderAction,
  ids: string[],
  values: z.output<ReturnType<typeof bulkSchema>>,
): PostAdminV1StoresStoreIdOrdersBulkBody {
  const optional = (value: string) => (value === '' ? null : value);
  switch (action) {
    case 'add_tags':
    case 'remove_tags':
      return { ids, action, payload: { tags: values.tags } };
    case 'mark_paid':
      return {
        ids,
        action,
        payload: { reference: optional(values.reference), note: optional(values.note) },
      };
    case 'fulfil':
      return {
        ids,
        action,
        payload: {
          carrier: optional(values.carrier),
          trackingNumber: optional(values.trackingNumber),
          trackingUrl: optional(values.trackingUrl),
          notifyCustomer: values.notifyCustomer,
        },
      };
    case 'cancel':
      return {
        ids,
        action,
        payload: { reason: values.reason, restock: values.restock, refund: values.refund },
      };
    case 'archive':
      return { ids, action };
  }
}

export interface BulkFailure {
  id: string;
  code: string | null;
  message: string | null;
}

export function bulkFailures(result: BulkOrdersResult): BulkFailure[] {
  return result.items
    .filter((item) => !item.ok)
    .map((item) => ({ id: item.id, code: item.errorCode, message: item.message }));
}
